import React, { FC, useEffect, useState } from 'react'
import queryString from 'query-string'

import ReactPaginate from 'react-paginate'

import OnboardingFlow from '../../onboarding/OnboardingFlow'
import Loading from '../../../Components/loading/Loading'
import './styles.scss'

import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Routes } from '../../../routing/routes'
import { Invitee, Meeting, MeetingStatus } from '../../../types/plann3r'
import { Avatar } from '../../../Components/avatar/Avatar'
import { requestCurrentMeetings, requestPastMeetings, setCurrentPage, setHistoryPage } from '../../../store/meeting-management/actions'
import { OnboardingSteps } from '../../../store/onboarding/types'
import { Plann3rStore } from '../../../store/types'
import { convertDateToLocalTime } from '../../../utils/general'

const MyMeetings : FC = () => {
  const [showModal, setshowModal] = useState(false)

  const dispatch = useDispatch()
  const history = useHistory()

  const strings = useSelector(
    (state: Plann3rStore) => state.languages.strings.meeting.mymeetings,
  )

  const { pending, current } = useSelector(
    (state: Plann3rStore) => state.meetingManagement,
  )

  const past = useSelector((state: Plann3rStore) => state.meetingManagement.history)

  const parsed = queryString.parse(window.location.hash)
  if (parsed.onboarding && parsed.onboarding === 'true') {
    setshowModal(true)
  }

  useEffect(() => {
    dispatch(requestCurrentMeetings())
    dispatch(requestPastMeetings())
  }, [dispatch])

  const closeModal = () => {
    setshowModal(false)
  }

  const renderCurrentMeeting = (meeting: Meeting): JSX.Element => {
    return (
      <tr
        key={meeting.id}
        onClick={() => history.push(`${Routes.MY_MEETINGS}/${meeting.id}`)}
      >
        <td className="first">
          {meeting.subject}
          <br />
          <span>
            {strings.meeting_created} {convertDateToLocalTime(meeting.created_at)}
          </span>
        </td>
        <td>
          {meeting.status === MeetingStatus.DECLINED && (
            <b style={{ color: 'indianred' }}>{strings.declined}</b>
          )}
          {meeting.status !== MeetingStatus.DECLINED &&
            meeting.picked_moment && <b>{convertDateToLocalTime(meeting.picked_moment.start)}</b>}
          {meeting.status !== MeetingStatus.DECLINED &&
            !meeting.picked_moment && <span>{strings.to_be_determined}</span>}
        </td>
        <td className="avatar-list">
          {meeting.invitees.map((invitee: Invitee) => (
            <Avatar
              key={invitee.email}
              url={invitee.avatar_url}
              label={
                invitee.first_name && invitee.last_name
                  ? invitee.first_name + ' ' + invitee.last_name
                  : invitee.email
              }
            />
          ))}
        </td>
      </tr>
    )
  }

  const renderCurrentMeetings = (meetings: Meeting[]) => {
    let currentMeetings
    if (meetings.length > 0) {
      currentMeetings = meetings.map(renderCurrentMeeting)
    } else {
      currentMeetings = (
        <tr>
          <td colSpan={3}>
            {current.isFetching ? <Loading /> : strings.no_meetings_yet}
          </td>
        </tr>
      )
    }

    return (
      <div>
        <table className="table table-meetings">
          <thead>
            <tr>
              <th>
                <p className="title is-3">{strings.current_meetings}</p>
              </th>
              <th>
                <p className="subtitle is-4">{strings.meeting_moment}</p>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>{currentMeetings}</tbody>
        </table>
        <div className="pagination">
          {current.meetings.length > current.items_per_page && (
            <ReactPaginate
              pageCount={current.meetings.length / current.items_per_page}
              initialPage={current.page}
              onPageChange={(page: {
                selected: number,
              }) => {
                dispatch(setCurrentPage(page.selected))
              }}
              pageRangeDisplayed={4}
              marginPagesDisplayed={1}
            />
          )}
        </div>
      </div>
    )
  }

  const renderPastMeeting = (meeting: Meeting) => {
    return (
      <tr key={meeting.id}>
        <td
          className="first"
          onClick={() => history.push(`${Routes.MY_MEETINGS}/${meeting.id}`)}
          role="presentation"
        >
          {meeting.subject}
          <br />
          <span>
            {strings.meeting_created} {convertDateToLocalTime(meeting.created_at)}
          </span>
        </td>
        <td
          onClick={() => history.push(`${Routes.MY_MEETINGS}/${meeting.id}`)}
          role="presentation"
        >
          <b>
            {meeting.picked_moment
              ? convertDateToLocalTime(meeting.picked_moment.start)
              : strings.no_moment_found}
          </b>
        </td>
        <td className="follow-up" />
      </tr>
    )
  }

  const renderPastMeetings = (meetings: Meeting[]) => {
    let pastMeetings

    if (past.isFetching === false && !meetings[0]) {
      return ''
    }

    if (past.isFetching === false && meetings[0]) {
      pastMeetings = meetings.map(renderPastMeeting)
    }

    return (
      <div>
        <table className="table table-meetings">
          <thead>
            <tr>
              <th>
                <p className="title is-3">{strings.history}</p>
              </th>
              <th>
                <p className="subtitle is-4" style={{ wordBreak: 'keep-all' }}>
                  {strings.meeting_date}
                </p>
              </th>
              <th />
            </tr>
          </thead>
          <tbody>{pastMeetings}</tbody>
        </table>
        <div className="pagination">
          {past.meetings.length > past.items_per_page && (
            <ReactPaginate
              pageCount={past.meetings.length / past.items_per_page}
              initialPage={past.page}
              onPageChange={(page: {
                selected: number,
              }) => {
                dispatch(setHistoryPage(page.selected))
              }}
              pageRangeDisplayed={4}
              marginPagesDisplayed={1}
            />
          )}
        </div>
      </div>
    )
  }

  const renderPendingMeeting = (meeting: Meeting) => {
    return (
      <div
        className="column is-3 content-box content-box-bottom has-text-centered pendingmeeting"
        key={meeting.id}
        onClick={() => history.push(`${Routes.MY_MEETINGS}/${meeting.id}`)}
        role="presentation"
      >
        <img
          src={
            meeting.avatar_url
              ? meeting.avatar_url
              : 'https://s3-us-west-2.amazonaws.com/plann3r.profilepictures/default.png'
          }
          alt={meeting.subject}
        />

        <p className="subtitle is-5">{meeting.subject}</p>
        <div>
          {strings.invited_by}{' '}
          <b>
            {meeting.owner_firstName} {meeting.owner_lastName}
          </b>
          <br />
          <button className="button fakeLink">{strings.view_more}</button>
        </div>
      </div>
    )
  }

  const renderPendingMeetings = (meetings: Meeting[]) => {
    if (meetings && meetings.length > 0) {
      const pendingMeetings = meetings.map(renderPendingMeeting)
      return (
        <div className="pendingmeetings">
          <p className="title is-3">{strings.pending_meetings}</p>
          <div className="columns" style={{ overflowX: 'auto' }}>
            {pendingMeetings}
          </div>
        </div>
      )
    }
  }

  const pendingMeetings = pending.meetings

  let page = current.page
  let items_per_page = current.items_per_page
  let start = page * items_per_page
  let end = start + items_per_page

  const currentMeetings = current.meetings.slice(start, end)

  page = past.page
  items_per_page = past.items_per_page
  start = page * items_per_page
  end = start + items_per_page

  const pastMeetings = past.meetings.slice(start, end)

  return (
    <div className="mymeetingscomponent">
      {renderPendingMeetings(pendingMeetings)}
      {renderCurrentMeetings(currentMeetings)}
      {renderPastMeetings(pastMeetings)}
      <div className={'modal onboarding' + (showModal ? ' is-active' : '')}>
        <div className="modal-background" onClick={closeModal} role="presentation" />
        <div className="modal-content">
          <div className="box">
            <OnboardingFlow
              headerVisible={true}
              new_steps={[
                OnboardingSteps.AGENDAS,
                OnboardingSteps.CONNECTED,
                OnboardingSteps.NAME,
                OnboardingSteps.LOCATION,
                OnboardingSteps.RANGES,
                OnboardingSteps.COMPLETED,
              ]}
            />
          </div>
        </div>
        <button className="modal-close" onClick={closeModal} />
      </div>
    </div>
  )
}

export default MyMeetings
